import { Component, OnInit, HostListener, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { SVGIcon, userOutlineIcon, logoutIcon, lockIcon } from '@progress/kendo-svg-icons';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../features/auth/services/auth.service';
import { BrandingService } from '../../../core/services/branding.service';
import { BaseComponent } from '../../base/base.component';
import { NotificationService } from '../../../core/services/notification.service';
import { User, UserService } from '../../../features/user/services/user.service';
import { UserChangePasswordModalComponent } from '../../components/user-management/user-change-password-modal/user-change-password-modal.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, ButtonsModule, IconsModule, UserChangePasswordModalComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent extends BaseComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);
  private elementRef = inject(ElementRef);
  private brandingService = inject(BrandingService);
  private cdr = inject(ChangeDetectorRef);
  private notificationService = inject(NotificationService);
  private userService = inject(UserService);

  logoImage: string | null = null;
  showUserMenu = false;
  showChangePasswordModal = false;
  userIconSvg: SVGIcon = userOutlineIcon;
  logoutIconSvg: SVGIcon = logoutIcon;
  lockIconSvg: SVGIcon = lockIcon;
  username = '';
  currentUser: User | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showUserMenu && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeUserMenu();
    }
  }

  ngOnInit(): void {
    this.brandingService.clearBranding();
    this.logoImage = null;

    this.brandingService.branding$.pipe(takeUntil(this.destroy$)).subscribe((branding) => {
      if (branding && branding.logoImage) {
        const base64 = branding.logoImage;
        if (base64.startsWith('data:')) {
          this.logoImage = base64;
        } else {
          this.logoImage = `data:image/jpeg;base64,${base64}`;
        }
      } else if (branding === null) {
        this.logoImage = null;
      }
      this.cdr.markForCheck();
    });
    this.username = this.extractUsernameFromToken();
    this.currentUser = this.buildCurrentUserFromToken();
  }

  private extractUsernameFromToken(): string {
    const token = this.authService.getToken();
    if (!token) return '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || payload.unique_name || payload.name || '';
    } catch {
      return '';
    }
  }

  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  closeUserMenu(): void {
    this.showUserMenu = false;
  }

  onLogout(): void {
    this.authService.logout();
    this.logoImage = null;
    this.router.navigate(['/auth/login']);
    this.closeUserMenu();
  }

  onChangePassword(): void {
    this.closeUserMenu();
    if (!this.currentUser) {
      this.notificationService.showError('Unable to identify current user.');
      return;
    }
    this.showChangePasswordModal = true;
  }

  closeChangePasswordModal(): void {
    this.showChangePasswordModal = false;
  }

  onSaveChangePassword(payload: { userId: string; newPassword: string }): void {
    this.userService.changePassword(payload.userId, payload.newPassword).subscribe({
      next: () => {
        this.showChangePasswordModal = false;
      },
      error: (_err) => {
        this.notificationService.showError('Failed to change password.');
      },
    });
  }

  private buildCurrentUserFromToken(): User | null {
    const token = this.authService.getToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId =
        payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
        payload.sub ||
        payload.userId ||
        payload.UserId ||
        '';
      if (!userId) {
        return null;
      }

      return {
        userId,
        username: this.username,
        email: '',
        firstName: '',
        lastName: '',
        isBlocked: false,
        deactivated: false,
        tenantName: '',
        isTenantAdmin: false,
      };
    } catch {
      return null;
    }
  }
}
